import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useGetPartnerLandingBrandingConfigQuery } from '@/services/ApiService';
import { parsePartnerLandingBrandingConfig, resolvePartnerLandingBranding } from '@/lib/partnerLandingBrandingResolve';
import type { PartnerLandingBranding } from '@/lib/partnerLandingBranding.types';
import { getPartnerLandingAttribution } from '@/lib/partnerLandingUrl';

export function usePartnerLandingBranding(search: string): PartnerLandingBranding | null {
    const navigate = useNavigate();
    const attribution = useMemo(() => getPartnerLandingAttribution(search), [search]);

    const {
        data: raw,
        isError,
        isLoading,
        isUninitialized,
    } = useGetPartnerLandingBrandingConfigQuery(undefined, {
        skip: !attribution.isKuderSource,
    });

    const parsedConfig = useMemo(() => {
        if (!attribution.isKuderSource || raw === undefined) {
            return null;
        }
        return parsePartnerLandingBrandingConfig(raw);
    }, [attribution.isKuderSource, raw]);

    useEffect(() => {
        if (!attribution.isKuderSource) {
            return;
        }
        if (isUninitialized || isLoading) {
            return;
        }
        if (isError) {
            navigate('/', { replace: true });
            return;
        }
        // `parsePartnerLandingBrandingConfig` rejects the entire document if any rule fails validation.
        // In that case `branding` stays null and we recover to `/` (UTMs dropped — fix the JSON).
        if (raw !== undefined && parsedConfig === null) {
            navigate('/', { replace: true });
        }
    }, [attribution.isKuderSource, isError, isLoading, isUninitialized, navigate, parsedConfig, raw]);

    return useMemo(() => {
        if (!attribution.isKuderSource) {
            return resolvePartnerLandingBranding(attribution, null);
        }
        if (isUninitialized || isLoading || isError || parsedConfig === null) {
            return null;
        }
        return resolvePartnerLandingBranding(attribution, parsedConfig);
    }, [attribution, isError, isLoading, isUninitialized, parsedConfig]);
}
